import type { Exercise } from '../exercise-types';

/** Lats, upper back, traps, biceps and forearms. */
export const PULL_EXERCISES: Exercise[] = [
  // ----------------------------------------------------------- vertical -----
  {
    id: 'pull_up',
    name: 'Pull-up',
    slots: ['back_vertical'],
    pattern: 'vertical_pull',
    primary: ['lats'],
    secondary: ['biceps', 'upper_back', 'forearms'],
    equipment: ['pullup_bar'],
    kind: 'compound',
    loadType: 'bodyweight',
    difficulty: 2,
    quality: 10,
    defaultSets: 4,
    repRange: [5, 10],
    restSeconds: 150,
    incrementKg: 2.5,
    setup: [
      'Overhand grip slightly wider than shoulders.',
      'Hang with straight arms, then pull your shoulder blades down to start the rep.',
    ],
    cues: [
      'Drive the elbows down and back towards your hips — think of pulling your chest to the bar.',
      'Full hang at the bottom every rep; half reps build half a back.',
      'Add load with a dip belt once you clear 10 clean reps.',
    ],
    mistakes: ['Kipping', 'Stopping short of a full hang'],
    variations: ['lat_pulldown_wide', 'assisted_pull_up', 'chin_up'],
    imagery: {
      start: 'hanging at full stretch from a pull-up bar with an overhand wide grip, arms straight, side view',
      end: 'pulled up with the chin above the bar, elbows driven down and back, chest towards the bar',
    },
  },
  {
    id: 'chin_up',
    name: 'Chin-up',
    slots: ['back_vertical', 'biceps_primary'],
    pattern: 'vertical_pull',
    primary: ['lats', 'biceps'],
    secondary: ['upper_back', 'forearms'],
    equipment: ['pullup_bar'],
    kind: 'compound',
    loadType: 'bodyweight',
    difficulty: 2,
    quality: 9,
    defaultSets: 3,
    repRange: [6, 12],
    restSeconds: 150,
    incrementKg: 2.5,
    setup: ['Underhand grip at shoulder width, full hang.'],
    cues: [
      'The supinated grip lets the biceps contribute hard — this is a genuine arm builder too.',
      'Chest to the bar, controlled 2-second negative.',
    ],
    variations: ['pull_up', 'neutral_grip_pulldown'],
    imagery: {
      start: 'hanging from a bar with an underhand shoulder width grip, arms fully straight',
      end: 'chin pulled above the bar with an underhand grip, elbows tight to the body',
    },
  },
  {
    id: 'lat_pulldown_wide',
    name: 'Wide-grip lat pulldown',
    slots: ['back_vertical'],
    pattern: 'vertical_pull',
    primary: ['lats'],
    secondary: ['biceps', 'upper_back'],
    equipment: ['lat_pulldown'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 9,
    defaultSets: 4,
    repRange: [8, 12],
    restSeconds: 120,
    incrementKg: 5,
    setup: [
      'Thigh pads snug so you do not lift off the seat.',
      'Grip wider than shoulders, overhand.',
    ],
    cues: [
      'Lean back maybe 15° and hold that angle — do not rock.',
      'Pull the bar to your upper chest, elbows down, then control it all the way back up to a full stretch.',
    ],
    mistakes: ['Pulling behind the neck', 'Using body english to move the stack'],
    variations: ['pull_up', 'neutral_grip_pulldown', 'assisted_pull_up'],
    imagery: {
      start: 'seated at a lat pulldown machine with arms fully extended overhead gripping a wide bar, lats stretched',
      end: 'lat pulldown bar pulled down to the upper chest, elbows driven down beside the ribs, slight lean back',
    },
  },
  {
    id: 'neutral_grip_pulldown',
    name: 'Neutral-grip pulldown',
    slots: ['back_vertical'],
    pattern: 'vertical_pull',
    primary: ['lats'],
    secondary: ['biceps', 'upper_back'],
    equipment: ['lat_pulldown'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 90,
    incrementKg: 5,
    setup: ['Swap to a V-bar or close neutral handle on the pulldown.'],
    cues: [
      'Palms facing each other gives the strongest line of pull and the easiest shoulders.',
      'Pull to the sternum, squeeze, full stretch on the return.',
    ],
    variations: ['lat_pulldown_wide', 'pull_up'],
    imagery: {
      start: 'seated at a pulldown machine holding a V-bar handle with palms facing each other, arms extended overhead',
      end: 'V-bar pulled down to the sternum with elbows tight to the sides, palms facing each other',
    },
  },
  {
    id: 'assisted_pull_up',
    name: 'Assisted pull-up',
    slots: ['back_vertical'],
    pattern: 'vertical_pull',
    primary: ['lats'],
    secondary: ['biceps', 'upper_back'],
    equipment: ['assisted_pullup'],
    kind: 'compound',
    loadType: 'assisted',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 120,
    incrementKg: 5,
    setup: ['Kneel or stand on the assist pad, set the counterweight so the last rep is genuinely hard.'],
    cues: ['Remember: on this machine a *higher* number on the stack means *easier*.'],
    variations: ['pull_up', 'lat_pulldown_wide'],
    imagery: {
      start: 'kneeling on the pad of an assisted pull-up machine, arms fully extended overhead gripping the bar',
      end: 'pulled up on an assisted pull-up machine with the chin level with the bar',
    },
  },

  // --------------------------------------------------------- horizontal -----
  {
    id: 'barbell_row',
    name: 'Barbell bent-over row',
    slots: ['back_horizontal'],
    pattern: 'horizontal_pull',
    primary: ['lats', 'upper_back'],
    secondary: ['biceps', 'delts_rear', 'lower_back'],
    equipment: ['barbell'],
    kind: 'compound',
    loadType: 'barbell',
    difficulty: 3,
    quality: 10,
    defaultSets: 4,
    repRange: [6, 10],
    restSeconds: 180,
    incrementKg: 2.5,
    setup: [
      'Hinge at the hips until your torso is about 45° or lower, knees softly bent.',
      'Bar hanging at arms length, overhand grip just outside the knees.',
      'Brace hard — flat back, chest up.',
    ],
    cues: [
      'Pull the bar to your lower ribs / upper stomach, elbows tracking back at about 45°.',
      'Keep the torso angle fixed. If your chest rises to help, the weight is too heavy.',
    ],
    mistakes: ['Rounding the lower back', 'Turning it into a half-deadlift'],
    variations: ['tbar_row', 'chest_supported_row', 'seated_cable_row'],
    imagery: {
      start: 'standing hinged forward at 45 degrees with a barbell hanging at arms length below the chest, flat back',
      end: 'barbell pulled to the lower ribs while hinged forward, elbows back at 45 degrees, flat back',
    },
  },
  {
    id: 'seated_cable_row',
    name: 'Seated cable row',
    slots: ['back_horizontal'],
    pattern: 'horizontal_pull',
    primary: ['lats', 'upper_back'],
    secondary: ['biceps', 'delts_rear'],
    equipment: ['seated_row_machine'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 120,
    incrementKg: 5,
    setup: ['Feet on the platform, soft knees, sit tall holding the V-handle.'],
    cues: [
      'Let the weight pull your shoulder blades forward at the front of each rep — that stretch matters.',
      'Pull to the navel, elbows brushing your sides, torso upright and still.',
    ],
    mistakes: ['Rowing with the lower back by swinging back and forth'],
    variations: ['barbell_row', 'chest_supported_row', 'machine_high_row'],
    imagery: {
      start: 'seated at a cable row machine with arms extended forward, shoulders reaching, torso upright',
      end: 'cable handle pulled to the navel with elbows driven straight back beside the ribs, chest up',
    },
  },
  {
    id: 'tbar_row',
    name: 'T-bar row',
    slots: ['back_horizontal'],
    pattern: 'horizontal_pull',
    primary: ['lats', 'upper_back'],
    secondary: ['biceps', 'lower_back'],
    equipment: ['tbar_row'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 2,
    quality: 9,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 150,
    incrementKg: 5,
    setup: ['Chest on the pad (or straddle the bar), neutral grip on the handles.'],
    cues: ['Drive the elbows back and squeeze the mid-back at the top.'],
    variations: ['barbell_row', 'chest_supported_row'],
    imagery: {
      start: 'chest against the pad of a T-bar row machine, arms hanging straight down holding the handles',
      end: 'T-bar row handles pulled to the chest with elbows driven back, shoulder blades squeezed',
    },
  },
  {
    id: 'dumbbell_row',
    name: 'Single-arm dumbbell row',
    slots: ['back_horizontal'],
    pattern: 'horizontal_pull',
    primary: ['lats'],
    secondary: ['biceps', 'upper_back'],
    equipment: ['dumbbells', 'flat_bench'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 9,
    unilateral: true,
    defaultSets: 3,
    repRange: [8, 12],
    restSeconds: 90,
    incrementKg: 2,
    setup: [
      'One knee and one hand on the bench, other foot planted, back flat and parallel to the floor.',
      'Dumbbell hanging at arms length.',
    ],
    cues: [
      'Row towards your hip, not your shoulder, and let the shoulder blade travel with it.',
      'Get a full stretch at the bottom — reach the weight towards the floor.',
    ],
    mistakes: ['Twisting the torso to heave the weight up'],
    variations: ['seated_cable_row', 'chest_supported_row', 'barbell_row'],
    imagery: {
      start: 'athlete braced with one knee and one hand on a flat bench, torso horizontal, free arm extended straight down holding a dumbbell',
      end: 'athlete braced on a flat bench having lifted the dumbbell up to waist level, elbow bent and pointing behind, torso horizontal',
    },
  },
  {
    id: 'chest_supported_row',
    name: 'Chest-supported dumbbell row',
    slots: ['back_horizontal'],
    pattern: 'horizontal_pull',
    primary: ['upper_back', 'lats'],
    secondary: ['delts_rear', 'biceps'],
    equipment: ['dumbbells', 'adjustable_bench'],
    kind: 'compound',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 90,
    incrementKg: 2,
    setup: ['Bench at ~30°, lie face down with your chest on the pad, dumbbells hanging.'],
    cues: [
      'With your chest supported you cannot cheat — all the work goes to the back.',
      'Row to the hips, pause, lower slow.',
    ],
    variations: ['seated_cable_row', 'tbar_row', 'machine_high_row'],
    imagery: {
      start: 'lying face down on a 30 degree incline bench with two dumbbells hanging straight down',
      end: 'dumbbells rowed up to the hips while lying face down on an incline bench, elbows back',
    },
  },
  {
    id: 'machine_high_row',
    name: 'Plate-loaded high row',
    slots: ['back_horizontal', 'back_vertical'],
    pattern: 'horizontal_pull',
    primary: ['lats'],
    secondary: ['upper_back', 'biceps'],
    equipment: ['high_row_machine'],
    kind: 'compound',
    loadType: 'machine',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 90,
    incrementKg: 5,
    setup: ['Chest on the pad, grip the high handles with arms extended up and forward.'],
    cues: ['Pull down and back towards your armpits, one arm at a time if you like.'],
    variations: ['lat_pulldown_wide', 'seated_cable_row'],
    imagery: {
      start: 'seated chest against the pad of a plate loaded high row machine, arms extended up and forward',
      end: 'high row handles pulled down and back towards the armpits, elbows low and behind the torso',
    },
  },
  {
    id: 'straight_arm_pulldown',
    name: 'Straight-arm pulldown',
    slots: ['back_isolation'],
    pattern: 'isolation',
    primary: ['lats'],
    secondary: ['triceps'],
    equipment: ['cable_station', 'straight_bar_attachment'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['High pulley, straight bar, step back and hinge forward slightly with arms overhead.'],
    cues: [
      'Keep the elbows almost locked and sweep the bar down to your thighs in a wide arc.',
      'The only lat exercise where the biceps cannot take over.',
    ],
    variations: ['dumbbell_pullover', 'lat_pulldown_wide'],
    imagery: {
      start: 'standing hinged slightly forward holding a high cable straight bar with arms extended overhead',
      end: 'straight bar swept down to the thighs with arms straight, lats contracted, slight forward lean',
    },
  },
  {
    id: 'dumbbell_pullover',
    name: 'Dumbbell pullover',
    slots: ['back_isolation', 'chest_isolation'],
    pattern: 'isolation',
    primary: ['lats'],
    secondary: ['chest', 'triceps'],
    equipment: ['dumbbells', 'flat_bench'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 2,
    quality: 7,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 75,
    incrementKg: 2,
    setup: ['Lie along or across a flat bench holding one dumbbell over your chest with both hands.'],
    cues: [
      'Lower it back behind your head with soft elbows until you feel a deep stretch through the lats and ribcage.',
      'Pull back over the chest without letting the elbows flare.',
    ],
    variations: ['straight_arm_pulldown'],
    imagery: {
      start: 'lying on a flat bench holding one dumbbell with both hands above the chest, arms nearly straight',
      end: 'dumbbell lowered back behind the head below bench level, arms extended, deep lat and ribcage stretch',
    },
  },

  // ------------------------------------------------------------- traps ------
  {
    id: 'barbell_shrug',
    name: 'Barbell shrug',
    slots: ['traps'],
    pattern: 'isolation',
    primary: ['traps'],
    secondary: ['forearms'],
    equipment: ['barbell'],
    kind: 'isolation',
    loadType: 'barbell',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [10, 15],
    restSeconds: 90,
    incrementKg: 5,
    setup: ['Bar at arms length in front of the thighs, overhand grip at shoulder width.'],
    cues: [
      'Shrug straight up towards your ears and hold for a beat at the top.',
      'No rolling the shoulders — that does nothing but grind the joint.',
    ],
    variations: ['dumbbell_shrug', 'cable_shrug', 'farmers_walk'],
    imagery: {
      start: 'standing holding a barbell at arms length against the thighs, shoulders relaxed and down',
      end: 'standing with shoulders shrugged straight up towards the ears holding a barbell, arms straight',
    },
  },
  {
    id: 'dumbbell_shrug',
    name: 'Dumbbell shrug',
    slots: ['traps'],
    pattern: 'isolation',
    primary: ['traps'],
    secondary: ['forearms'],
    equipment: ['dumbbells'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 75,
    incrementKg: 2,
    setup: ['Heavy dumbbells at your sides, arms straight, chest up.'],
    cues: [
      'Dumbbells at your sides give a slightly better line of pull than a bar in front.',
      'Pause at the top, lower until you feel the traps stretch.',
    ],
    variations: ['barbell_shrug', 'farmers_walk'],
    imagery: {
      start: 'standing with a heavy dumbbell in each hand at the sides, shoulders relaxed down',
      end: 'standing with shoulders shrugged high towards the ears holding dumbbells at the sides',
    },
  },
  {
    id: 'cable_shrug',
    name: 'Cable shrug',
    slots: ['traps'],
    pattern: 'isolation',
    primary: ['traps'],
    secondary: [],
    equipment: ['cable_station', 'straight_bar_attachment'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [12, 18],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Low pulley with a straight bar, stand upright holding it at arms length.'],
    cues: ['Constant tension at the bottom, which free weights lose. Squeeze up, control down.'],
    variations: ['barbell_shrug', 'dumbbell_shrug'],
    imagery: {
      start: 'standing upright holding a low cable straight bar at arms length, shoulders relaxed down',
      end: 'shoulders shrugged up towards the ears while holding a low cable bar at arms length',
    },
  },
  {
    id: 'farmers_walk',
    name: "Farmer's walk",
    slots: ['traps', 'forearms', 'finisher'],
    pattern: 'carry',
    primary: ['traps', 'forearms'],
    secondary: ['abs', 'obliques', 'glutes'],
    equipment: ['dumbbells'],
    kind: 'compound',
    loadType: 'timed',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [30, 45],
    restSeconds: 90,
    incrementKg: 2,
    setup: ['Pick up the heaviest pair you can hold, stand tall.'],
    cues: [
      'Chest up, ribs down, short controlled steps for 30–45 seconds.',
      'Reps here are seconds, not reps. Grip and traps get hammered at once.',
    ],
    variations: ['dumbbell_shrug', 'suitcase_carry'],
    imagery: {
      start: 'standing tall holding a heavy dumbbell in each hand at the sides, ready to walk, side view',
      end: 'mid stride walking forward carrying heavy dumbbells at the sides, torso upright and braced',
    },
  },

  // ------------------------------------------------------------ biceps ------
  {
    id: 'barbell_curl',
    name: 'Barbell curl',
    slots: ['biceps_primary'],
    pattern: 'isolation',
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: ['barbell'],
    kind: 'isolation',
    loadType: 'barbell',
    difficulty: 1,
    quality: 9,
    defaultSets: 4,
    repRange: [8, 12],
    restSeconds: 90,
    incrementKg: 2.5,
    setup: ['Shoulder-width underhand grip, bar resting against the thighs, elbows at your sides.'],
    cues: [
      'Elbows stay pinned — if they swing forward, your front delts are doing it.',
      'Curl to just past 90°, squeeze, then lower over 2–3 seconds to full extension.',
    ],
    mistakes: ['Swinging the hips to start the rep', 'Half reps at the bottom'],
    variations: ['ez_bar_curl', 'dumbbell_curl', 'cable_curl'],
    imagery: {
      start: 'standing holding a barbell at arms length against the thighs with an underhand shoulder width grip',
      end: 'barbell curled up to chest height with elbows pinned at the sides, biceps contracted',
    },
  },
  {
    id: 'ez_bar_curl',
    name: 'EZ-bar curl',
    slots: ['biceps_primary'],
    pattern: 'isolation',
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: ['ez_bar'],
    kind: 'isolation',
    loadType: 'barbell',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [10, 12],
    restSeconds: 75,
    incrementKg: 2.5,
    setup: ['Take the inner angled grips of the EZ bar, palms up.'],
    cues: ['The angled grip is much kinder on the wrists than a straight bar. Same rules otherwise.'],
    variations: ['barbell_curl', 'preacher_curl', 'cable_curl'],
    imagery: {
      start: 'standing holding an EZ curl bar at arms length against the thighs, angled underhand grip',
      end: 'EZ curl bar curled to chest height with elbows fixed at the sides',
    },
  },
  {
    id: 'dumbbell_curl',
    name: 'Alternating dumbbell curl',
    slots: ['biceps_primary', 'biceps_secondary'],
    pattern: 'isolation',
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: ['dumbbells'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 75,
    incrementKg: 2,
    setup: ['Dumbbells at your sides, palms facing in.'],
    cues: [
      'Rotate the palm up as you curl — that supination is extra biceps work a bar cannot give you.',
      'One arm at a time, no body swing.',
    ],
    variations: ['barbell_curl', 'incline_dumbbell_curl', 'hammer_curl'],
    imagery: {
      start: 'standing with two dumbbells hanging at the sides, palms facing inward towards the body',
      end: 'one dumbbell curled up to the shoulder with the palm rotated to face up, other arm still down',
    },
  },
  {
    id: 'cable_curl',
    name: 'Cable curl',
    slots: ['biceps_primary', 'biceps_secondary'],
    pattern: 'isolation',
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: ['cable_station', 'straight_bar_attachment'],
    kind: 'isolation',
    loadType: 'cable',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Low pulley with a straight or EZ attachment, stand a step back from the machine.'],
    cues: ['Tension never drops, top or bottom. Great for finishing biceps off.'],
    variations: ['barbell_curl', 'ez_bar_curl'],
    imagery: {
      start: 'standing a step back from a low cable pulley holding a straight bar at arms length, arms extended',
      end: 'cable bar curled up to chest height with elbows at the sides, constant cable tension',
    },
  },
  {
    id: 'incline_dumbbell_curl',
    name: 'Incline dumbbell curl',
    slots: ['biceps_secondary'],
    pattern: 'isolation',
    primary: ['biceps'],
    secondary: [],
    equipment: ['dumbbells', 'adjustable_bench'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 75,
    incrementKg: 2,
    setup: ['Bench at about 60°, sit back, let your arms hang straight down behind your torso.'],
    cues: [
      'That hanging start puts the long head of the biceps on a deep stretch — this is why it grows arms.',
      'Do not let the elbows drift forward as you curl.',
    ],
    variations: ['dumbbell_curl', 'preacher_curl'],
    imagery: {
      start: 'seated leaning back on a 60 degree incline bench with dumbbells hanging straight down behind the torso',
      end: 'dumbbells curled up to shoulder height while leaning back on an incline bench, elbows still back',
    },
  },
  {
    id: 'preacher_curl',
    name: 'Preacher curl',
    slots: ['biceps_secondary'],
    pattern: 'isolation',
    primary: ['biceps'],
    secondary: ['forearms'],
    equipment: ['preacher_bench', 'ez_bar'],
    kind: 'isolation',
    loadType: 'barbell',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [10, 12],
    restSeconds: 75,
    incrementKg: 2.5,
    setup: ['Armpits over the top of the pad, upper arms flat against it.'],
    cues: [
      'Lower all the way to a straight arm — controlled, this is where the growth is.',
      'Do not bounce out of the bottom; the stretched position is vulnerable.',
    ],
    variations: ['incline_dumbbell_curl', 'machine_curl', 'ez_bar_curl'],
    imagery: {
      start: 'seated at a preacher bench with upper arms on the pad and an EZ bar held at nearly full arm extension',
      end: 'EZ bar curled up to chin height with upper arms flat on the preacher pad',
    },
  },
  {
    id: 'hammer_curl',
    name: 'Hammer curl',
    slots: ['biceps_secondary', 'forearms'],
    pattern: 'isolation',
    primary: ['biceps', 'forearms'],
    secondary: [],
    equipment: ['dumbbells'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 9,
    defaultSets: 3,
    repRange: [10, 14],
    restSeconds: 60,
    incrementKg: 2,
    setup: ['Dumbbells at your sides, palms facing each other, and keep them that way.'],
    cues: [
      'The neutral grip loads the brachialis, which sits under the biceps and pushes it up as it grows.',
      'This is the exercise that makes arms look thick from the side.',
    ],
    variations: ['dumbbell_curl', 'reverse_curl'],
    imagery: {
      start: 'standing with two dumbbells at the sides held with a neutral grip, palms facing each other',
      end: 'dumbbells curled up to shoulder height with palms still facing each other, neutral grip',
    },
  },
  {
    id: 'concentration_curl',
    name: 'Concentration curl',
    slots: ['biceps_secondary'],
    pattern: 'isolation',
    primary: ['biceps'],
    secondary: [],
    equipment: ['dumbbells', 'flat_bench'],
    kind: 'isolation',
    loadType: 'dumbbell',
    difficulty: 1,
    quality: 7,
    unilateral: true,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 45,
    incrementKg: 2,
    setup: ['Sit on the bench, elbow braced against the inside of your thigh, dumbbell hanging.'],
    cues: ['Zero cheating possible. Curl up, squeeze hard, lower slowly.'],
    variations: ['incline_dumbbell_curl', 'machine_curl'],
    imagery: {
      start: 'seated on a bench leaning forward with the elbow braced on the inner thigh, dumbbell hanging down',
      end: 'dumbbell curled up towards the shoulder with the elbow still braced against the inner thigh',
    },
  },
  {
    id: 'machine_curl',
    name: 'Machine biceps curl',
    slots: ['biceps_secondary'],
    pattern: 'isolation',
    primary: ['biceps'],
    secondary: [],
    equipment: ['biceps_curl_machine'],
    kind: 'isolation',
    loadType: 'machine',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Seat and pad set so your elbows line up with the machine pivot.'],
    cues: ['Good last exercise — safe to push to failure without technique falling apart.'],
    variations: ['preacher_curl', 'cable_curl'],
    imagery: {
      start: 'seated at a biceps curl machine with upper arms on the pad and arms extended holding the handles',
      end: 'seated at a biceps curl machine with the handles curled up towards the shoulders',
    },
  },

  // ----------------------------------------------------------- forearms -----
  {
    id: 'reverse_curl',
    name: 'Reverse curl',
    slots: ['forearms'],
    pattern: 'isolation',
    primary: ['forearms'],
    secondary: ['biceps'],
    equipment: ['ez_bar'],
    kind: 'isolation',
    loadType: 'barbell',
    difficulty: 1,
    quality: 8,
    defaultSets: 3,
    repRange: [12, 15],
    restSeconds: 60,
    incrementKg: 1,
    setup: ['EZ bar with an overhand grip, arms straight, bar against the thighs.'],
    cues: [
      'Curl with the knuckles leading and the wrists straight.',
      'Use much less weight than a normal curl — that is expected, not a problem.',
    ],
    variations: ['hammer_curl', 'barbell_wrist_curl'],
    imagery: {
      start: 'standing holding an EZ bar with an overhand grip at arms length against the thighs',
      end: 'EZ bar curled up to chest height with an overhand grip, knuckles leading, wrists straight',
    },
  },
  {
    id: 'barbell_wrist_curl',
    name: 'Seated wrist curl',
    slots: ['forearms'],
    pattern: 'isolation',
    primary: ['forearms'],
    secondary: [],
    equipment: ['fixed_barbells', 'flat_bench'],
    kind: 'isolation',
    loadType: 'barbell',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [15, 20],
    restSeconds: 45,
    incrementKg: 2.5,
    setup: ['Sit with your forearms flat on your thighs, wrists just past your knees, palms up.'],
    cues: [
      'Let the bar roll down to your fingertips, then curl it back up and squeeze.',
      'High reps only. Forearms recover fast and respond to volume.',
    ],
    variations: ['behind_back_wrist_curl', 'reverse_curl'],
    imagery: {
      start: 'seated with forearms resting on the thighs, palms up, a barbell rolled down into the fingertips',
      end: 'seated with forearms on the thighs and the wrists curled up, barbell lifted, forearms contracted',
    },
  },
  {
    id: 'behind_back_wrist_curl',
    name: 'Behind-the-back wrist curl',
    slots: ['forearms'],
    pattern: 'isolation',
    primary: ['forearms'],
    secondary: [],
    equipment: ['barbell'],
    kind: 'isolation',
    loadType: 'barbell',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [15, 20],
    restSeconds: 45,
    incrementKg: 2.5,
    setup: ['Stand holding a barbell behind your glutes with an overhand grip.'],
    cues: ['Roll the bar down the fingers, then curl the wrists up. Small range, big burn.'],
    variations: ['barbell_wrist_curl'],
    imagery: {
      start: 'standing holding a barbell behind the back at the glutes, wrists extended down, bar in the fingers',
      end: 'standing with the barbell behind the back and the wrists curled upward, forearms flexed',
    },
  },
  {
    id: 'plate_pinch',
    name: 'Plate pinch hold',
    slots: ['forearms'],
    pattern: 'carry',
    primary: ['forearms'],
    secondary: [],
    equipment: ['plates'],
    kind: 'isolation',
    loadType: 'timed',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [20, 40],
    restSeconds: 60,
    incrementKg: 1.25,
    setup: ['Pinch two smooth plates together between thumb and fingers, one in each hand.'],
    cues: ['Hold for time, stand tall. Reps are seconds. Brutal for grip.'],
    variations: ['dead_hang', 'farmers_walk'],
    imagery: {
      start: 'standing holding two weight plates pinched together between thumb and fingers at the side',
      end: 'standing still gripping pinched weight plates at the sides, forearms visibly working',
    },
  },
  {
    id: 'dead_hang',
    name: 'Dead hang',
    slots: ['forearms'],
    pattern: 'carry',
    primary: ['forearms'],
    secondary: ['lats'],
    equipment: ['pullup_bar'],
    kind: 'isolation',
    loadType: 'timed',
    difficulty: 1,
    quality: 7,
    defaultSets: 3,
    repRange: [30, 60],
    restSeconds: 60,
    incrementKg: 2.5,
    setup: ['Hang from a pull-up bar with a full overhand grip, arms straight.'],
    cues: [
      'Stay relaxed through the shoulders but do not let them come up to your ears.',
      'Reps are seconds. Decompresses the spine as a bonus.',
    ],
    variations: ['plate_pinch', 'farmers_walk'],
    imagery: {
      start: 'hanging from a pull-up bar with straight arms and an overhand grip, body relaxed, side view',
      end: 'still hanging from a pull-up bar with straight arms, shoulders packed down, body vertical',
    },
  },
];
